/**
 * DataInterpreter + Files (Knowledge Base "Agent Knowledge" tab, plus its
 * assistant-memory panel) + SharedKnowledge hooks.
 *
 * All three tabs shared the same raw-axios pattern: a paginated GET list
 * keyed on local `page` state with page-change handlers that imperatively
 * refetched, and a delete that looped sequentially (awaited, not
 * parallelized) over selected ids before refreshing the list. useQuery keyed
 * on the page number replaces the imperative refetch-on-page-change — paging
 * is just a new query key. Each delete keeps its sequential loop (the
 * backend takes one id at a time; nothing here suggested batching was safe)
 * inside a single mutation whose onSuccess invalidates the list once, rather
 * than once per deleted id.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST, DELETE } = ReqMethods;

const toIdArray = (ids) => (Array.isArray(ids) ? ids : [ids]);

// components/uploadFiles/index.js's media library picker (getMediaFiles),
// fired once on mount. Kept as a mutation (not a query) since the caller
// chains an extra download-and-convert-to-File step on the response before
// storing it in state, matching the mutateAsync-in-an-effect pattern used
// elsewhere for on-mount reads with imperative post-processing.
export function useMediaLibraryFiles() {
  return useMutation({
    mutationFn: (locationId) =>
      request(GET, "/assistant/files/media-library", {
        query: `altId=${locationId}&altType=location&objectId=${locationId}&whichObject=account`,
      }),
  });
}

// ---- Data Interpreter ---------------------------------------------------

export const dataInterpreterKeys = {
  all: ["dataInterpreterFiles"],
  list: (profileId, page) => [...dataInterpreterKeys.all, "list", profileId, page],
};

export function useDataInterpreterFiles(profileId, page) {
  return useQuery({
    queryKey: dataInterpreterKeys.list(profileId, page),
    queryFn: async () => {
      const query = `profile_id=${profileId}&page=${page}&limit=10`;
      const res = await request(GET, "/chat/get-data-interpreter-files", { query });
      return { files: res?.data || [], pagination: res?.pagination || {} };
    },
    enabled: !!profileId,
  });
}

// mutate with { ids, assistantIds } — assistantIds is sent as-is for every id
// in the batch, matching the original loop (it reused whatever single
// assistant_id was resolved from the confirmed file, not a per-file lookup).
export function useDeleteDataInterpreterFiles(profileId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, assistantIds }) => {
      for (const fileId of toIdArray(ids)) {
        await request(POST, "/chat/delete-interperter-file", {
          payload: { file_id: fileId, assistant_ids: assistantIds },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dataInterpreterKeys.all });
    },
  });
}

// ---- Knowledge Base files (Agent Knowledge tab) -------------------------

export const knowledgeBaseFilesKeys = {
  all: ["knowledgeBaseFiles"],
  list: (profileId, userId, page) => [...knowledgeBaseFilesKeys.all, "list", profileId, userId, page],
};

export function useKnowledgeBaseFiles(profileId, userId, page) {
  return useQuery({
    queryKey: knowledgeBaseFilesKeys.list(profileId, userId, page),
    queryFn: async () => {
      const query = `profile_id=${profileId}&user_id=${userId}&page=${page}`;
      const res = await request(GET, "/chat/assistants/files", { query });
      return { files: res?.data?.data || [], pagination: res?.pagination || {} };
    },
    enabled: !!profileId,
  });
}

export function useDeleteKnowledgeBaseFiles(profileId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids) => {
      for (const fileId of toIdArray(ids)) {
        await request(POST, "/chat/assistants/files/delete", { payload: { file_id: fileId } });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: knowledgeBaseFilesKeys.all });
    },
  });
}

// ---- Assistant memory (Agent Knowledge tab) ------------------------------

export const assistantMemoryKeys = {
  all: ["assistantMemory"],
  detail: (profileId) => [...assistantMemoryKeys.all, profileId],
};

export function useAssistantMemory(profileId) {
  return useQuery({
    queryKey: assistantMemoryKeys.detail(profileId),
    queryFn: async () => {
      const res = await request(GET, "/chat/assistants/memory", { query: `profile_id=${profileId}` });
      // React Query forbids a queryFn resolving to undefined (e.g. a
      // freshly-created profile with no memory content yet) — null means
      // "no memory," matching what the original useState(null) defaulted to.
      return res?.data?.data ?? null;
    },
    enabled: !!profileId,
  });
}

export function useUpdateAssistantMemory(profileId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (content) =>
      request(POST, "/chat/assistants/memory", {
        payload: { profile_id: profileId, data: JSON.stringify({ content }) },
      }).then((res) => res?.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assistantMemoryKeys.detail(profileId) });
    },
  });
}

export function useDeleteAssistantMemory(profileId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      request(POST, "/chat/assistants/memory/delete", {
        payload: { profile_id: profileId },
      }).then((res) => res?.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assistantMemoryKeys.detail(profileId) });
    },
  });
}

// ---- Shared Knowledge -----------------------------------------------------

export const sharedKnowledgeKeys = {
  all: ["sharedKnowledgeFiles"],
  list: (profileId) => [...sharedKnowledgeKeys.all, "list", profileId],
};

export function useSharedKnowledgeFiles(profileId) {
  return useQuery({
    queryKey: sharedKnowledgeKeys.list(profileId),
    queryFn: async () => {
      const res = await request(GET, `/chat/profiles/${profileId}/files`);
      return Array.isArray(res?.data) ? res.data : [];
    },
    enabled: !!profileId,
  });
}

export function useDeleteSharedKnowledgeFiles(profileId, userId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (ids) => {
      for (const fileId of toIdArray(ids)) {
        await request(DELETE, `/chat/profiles/${profileId}/files/${fileId}`, {
          query: `user_id=${userId}`,
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sharedKnowledgeKeys.list(profileId) });
    },
  });
}
